import React from 'react';
import { motion } from 'framer-motion';
import { Star, Mail, Bell, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { PageSEO } from '../components/SEO';

const SQAWK_Q_LOGO = "https://customer-assets.emergentagent.com/job_sqawk-social-4/artifacts/su4m18ey_ChatGPT%20Image%20Jan%2027%2C%202026%2C%2008_01_31%20PM.png";
const SQAWK_MAIN_LOGO = "https://customer-assets.emergentagent.com/job_sqawk-social-4/artifacts/ChatGPT%20Image%20Jan%2027%2C%202026%2C%2008_27_41%20PM.png";

const PendingApproval = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4" data-testid="pending-approval-page">
      <PageSEO.PendingApproval />
      {/* Animated stars background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(50)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              opacity: [0.2, 1, 0.2],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 2 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 max-w-md w-full"
      >
        {/* Main Card */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-3xl border border-slate-700/50 shadow-2xl overflow-hidden">
          {/* Header with glow */}
          <div className="relative p-8 text-center">
            {/* Glowing Q Logo */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
              className="relative inline-block mb-6"
            >
              <div className="absolute inset-0 bg-emerald-500/30 blur-3xl rounded-full" />
              <img 
                src={SQAWK_Q_LOGO} 
                alt="sQawk" 
                className="w-28 h-28 rounded-full relative z-10 border-4 border-emerald-500/50 shadow-lg shadow-emerald-500/30"
              />
              <motion.div
                className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center"
                animate={{ rotate: 360 }}
                transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
              >
                <Star className="w-4 h-4 text-white fill-white" />
              </motion.div>
            </motion.div>

            {/* Message */}
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-2xl font-bold text-white mb-4"
            >
              Thank You for Your Inquiry!
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-slate-300 text-lg leading-relaxed"
            >
              The universe is reviewing your profile and you will receive an email and notification shortly, if approved. <span className="text-2xl">:)</span>
            </motion.p>
          </div>

          {/* Info Cards */}
          <div className="px-8 pb-8 space-y-4">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="flex items-center gap-4 p-4 bg-slate-700/30 rounded-xl border border-slate-600/30"
            >
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-white font-medium">Check Your Email</h3>
                <p className="text-slate-400 text-sm">We'll send you a welcome email when you're approved</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 }}
              className="flex items-center gap-4 p-4 bg-slate-700/30 rounded-xl border border-slate-600/30"
            >
              <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                <Bell className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-white font-medium">Push Notification</h3>
                <p className="text-slate-400 text-sm">You'll get a notification on your device too</p>
              </div>
            </motion.div>
          </div>

          {/* Footer */}
          <div className="bg-slate-900/50 px-8 py-6 border-t border-slate-700/50">
            <div className="flex items-center justify-center gap-4 mb-4">
              <img src={SQAWK_MAIN_LOGO} alt="sQawk" className="h-10 w-auto opacity-80" />
            </div>
            <p className="text-center text-slate-500 text-xs">
              sQawk is<br/>
              <span className="text-emerald-400">Social Media for People.</span>
            </p>
          </div>
        </div>

        {/* Back to Home Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-6 text-center"
        >
          <Button
            variant="ghost"
            className="text-slate-400 hover:text-white"
            onClick={() => navigate('/')}
            data-testid="back-home-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default PendingApproval;
